import styles from "./SubscriptionItem.module.scss";
import { ISubscription } from "../../../../../models/Subscription";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import CustomButton from "../../../../../components/ui/CustomButton/CustomButton";
import useAppSelector from "../../../../../hooks/useAppSelector";
import useAppDispatch from "../../../../../hooks/useAppDispatch";
import ConfirmPopup from "../../../../../components/shared/ConfirmPopup/ConfirmPopup";
import attentionIcon from "../../../../../assets/images/icons/attention.svg";
import buySubscriptionIcon from "../../../../../assets/images/icons/buy-subscription.svg";
import { useNavigate } from "react-router-dom";
import { getSubscriptionDurationType } from "../../../../../helper/getSubscriptionDurationType";
import { useGetUserSubscriptionQuery } from "../../../../../api/queries";
import SelectPaymentMethodPopup from "../../../../../components/SelectPaymentMethodPopup/SelectPaymentMethodPopup";
import FullScreenLoader from "../../../../../components/shared/FullScreenLoader/FullScreenLoader";
import { useStripeMutations } from "../../../../../api/mutations";
import { handleError } from "../../../../../helper/errorHandler";
import { loadStripe } from "@stripe/stripe-js";
import { openErrorMessage } from "../../../../../store/message/messageSlice";
import PaymentResultPopup from "../../../../../components/shared/PaymentResultPopup/PaymentResultPopup";

interface ISubscriptionItemProps {
  item: ISubscription;
  stripePublicKey: string;
}

const SubscriptionItem: React.FC<ISubscriptionItemProps> = ({ item, stripePublicKey }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const { user } = useAppSelector((state) => state.userProfileSlice);
  const { data: userSubscription, refetch: refetchGetUserSubscription } =
    useGetUserSubscriptionQuery(user?.id as string);
  const { changeSubscription } = useStripeMutations();

  const [showMore, setShowMore] = useState<boolean>(false);
  const [showSelectPaymentMethodPopup, setShowSelectPaymentMethodPopup] = useState<boolean>(false);
  const [showConfirmPopup, setShowConfirmPopup] = useState({
    isOpen: false,
    title: "",
    description: "",
    icon: attentionIcon,
  });
  const [loading, setLoading] = useState<boolean>(false);
  const [typeOfPaymentResultPopup, setTypeOfPaymentResultPopup] = useState<
    "success" | "failed" | null
  >(null);

  const clickOnSelectPlan = () => {
    if (!userSubscription) {
      setShowSelectPaymentMethodPopup(true);
    } else {
      setShowConfirmPopup({
        isOpen: true,
        title: t("ChangeYourSubscription") + "!",
        description: t("AreYouSureYouWantToChangeYourSubscription"),
        icon: attentionIcon,
      });
    }
  };

  const changeSubscriptionFn = async () => {
    if (!user) {
      return;
    }
    setLoading(true);
    changeSubscription(
      { subscriptionId: item.id },
      {
        onSuccess: async (res) => {
          if (res.paymentCompleted) {
            setLoading(false);
            setTypeOfPaymentResultPopup("success");
            refetchGetUserSubscription();
          }

          if (res.clientSecret) {
            const stripeInstance = await loadStripe(stripePublicKey);
            if (!stripeInstance) {
              setTypeOfPaymentResultPopup("failed");
              setLoading(false);
              return;
            }

            const { error, paymentIntent } = await stripeInstance.confirmCardPayment(
              res.clientSecret,
              { payment_method: res.paymentMethodId }
            );

            if (error) {
              setTypeOfPaymentResultPopup("failed");
              setLoading(false);
              dispatch(openErrorMessage(error.message ?? "Payment failed"));
              return;
            }

            if (paymentIntent?.status === "succeeded") {
              setTypeOfPaymentResultPopup("success");
              setLoading(false);
              refetchGetUserSubscription();
            } else {
              setTypeOfPaymentResultPopup("failed");
              setLoading(false);
            }
          }
        },
        onError: (e) => {
          setLoading(false);
          setTypeOfPaymentResultPopup("failed");
          handleError(e, dispatch, t);
        },
      }
    );
  };

  const hideConfirmPopup = () => {
    setShowConfirmPopup({ isOpen: false, title: "", description: "", icon: attentionIcon });
  };

  const changeShowMoreVisible = () => {
    setShowMore(!showMore);
  };

  return (
    <>
      <div className={styles.item}>
        <div className={styles.box}>
          <h3 className={styles.name}>{item.name}</h3>
          <h3 className={styles.credits}>
            {item.credits} {t("Credits")}
          </h3>
          <p className={styles.price}>
            €{item.price} / {item.duration}{" "}
            {getSubscriptionDurationType(item.durationType, item.duration, t)}
          </p>
          {item.description.length > 37 && !showMore ? (
            <p className={styles.description}>
              {item.description.slice(0, 33)}{" "}
              <span onClick={changeShowMoreVisible} className={styles.showMore}>
                {t("ShowMore")}..
              </span>
            </p>
          ) : (
            <p className={styles.description}>
              {item.description}
              {showMore && (
                <span onClick={changeShowMoreVisible} className={styles.showMore}>
                  {t("ShowLess")}
                </span>
              )}
            </p>
          )}
        </div>
        <div className={styles.btnWrapper}>
          <CustomButton
            functionOnClick={clickOnSelectPlan}
            text={t("SelectThisPlan")}
            type={"button"}
            typeStyle={"fill"}
          />
        </div>
      </div>
      {showConfirmPopup.isOpen && (
        <ConfirmPopup
          iconPath={showConfirmPopup.icon}
          title={showConfirmPopup.title}
          description={showConfirmPopup.description}
          onClickYes={changeSubscriptionFn}
          onClickNo={hideConfirmPopup}
        />
      )}
      {showSelectPaymentMethodPopup && (
        <SelectPaymentMethodPopup
          onClose={() => setShowSelectPaymentMethodPopup(false)}
          purchasingItemId={item.id}
          purchasingItemType={"subscription"}
          stripePublicKey={stripePublicKey}
        />
      )}
      {loading && <FullScreenLoader />}
      {typeOfPaymentResultPopup && (
        <PaymentResultPopup
          type={typeOfPaymentResultPopup}
          closeFn={() => {
            setTypeOfPaymentResultPopup(null);
            hideConfirmPopup();
          }}
        />
      )}
    </>
  );
};

export default SubscriptionItem;
